/* eslint-disable eqeqeq */
import { useCallback, useEffect, useState, useMemo } from 'react';
import useObservable from '../UseObservable';
import CenterService, { defaultCenter } from '../../services/CenterService';
import { setCoreAuthToken } from '../../api/CoreInstance';
import { useRouter } from '../../routes';
import { setLocalStorageItem } from 'lib/helpers';
import { LOCAL_ACCOUNT_TOKEN, LOCAL_ACTIVE_CENTER_TOKEN, LOCAL_CENTER_ID, LOCAL_CENTER_EXTERNAL_ID } from 'constants/localStorage';
import { findItemById } from '../../lib/helpers';
import { KEYS_LOCALSTORAGE } from 'constants/general';
import EventReporter from 'appEvents';
import { mixpanel } from 'appEvents/providers';

interface IUseCenters {
  autoFetchs?: boolean
}

const setCenterInformation = (_activeCenter: any) => {
  setLocalStorageItem(KEYS_LOCALSTORAGE.ACTIVE_CENTER_NAME, _activeCenter?.name);
  setLocalStorageItem(KEYS_LOCALSTORAGE.ACTIVE_CENTER_ID, _activeCenter?.id);
  setLocalStorageItem(KEYS_LOCALSTORAGE.ACTIVE_CENTER_EXTERNAL_ID, _activeCenter?.external_id);
  EventReporter.setUserAttributes({ center_name: _activeCenter?.name });
  mixpanel.getInstance().register({ center_name: _activeCenter?.name });
};

const centerService = CenterService.getInstance();

function useCenters(
  { autoFetchs }: IUseCenters = { autoFetchs: false },
) {
  const { currentParamsRef, addParamsToUrl } = useRouter();
  const { centerId } = currentParamsRef.current;
  const [center, setCenter]: any = useState();
  const [centerConfig, setCenterConfig] = useState({});
  const [activeCenterConfig, setActiveCenterConfig] = useState<any>(undefined);
  const [centerList = []] = useObservable(centerService.getCenterList$(), centerService.setCenterList$);
  const [loadingCenters$] = useObservable(centerService.getLoadingCenters$());
  const [activeCenter = defaultCenter] = useObservable(centerService.getActiveCenter$());
  const [loadingCenters, setLoadingCenters] = useState(false);
  const centerToken = activeCenter?.kinedu_classrooms_token?.access_token;
  const fetchDetailedCenters = () => centerService.fetchDetailedCenters();

  const setActiveCenterById = useCallback((_centerId?: number, _center?: any, _centerList?: any) => {
    const centerToSearch = _centerId || centerId || Number(LOCAL_CENTER_ID());
    const foundCenter = findItemById(_centerList || centerList, centerToSearch);
    const centerToSet = foundCenter || centerList?.[0] || _center;
    centerService.setActiveCenter$(centerToSet);
    setCenterInformation(centerToSet);
    addParamsToUrl({ centerId: centerToSet.id });
  }, [addParamsToUrl, centerId, centerList]);

  const fetchCenters = useCallback(async () => {
    setLoadingCenters(true);
    try {
      const centers: any = await centerService.fetchCenters();
      setLoadingCenters(false);
      setActiveCenterById(centerId || centers[0].id, centers[0], centers);

      return centers;
    } catch (error) {
      setLoadingCenters(false);
      return [];
    }
  }, [centerId, setActiveCenterById]);

  const fetchCenter = useCallback(async () => {
    setLoadingCenters(true);
    try {
      const { center: centerValue } = await centerService.fetchCenter(activeCenter.id);
      const { myCenter, ...rest } = await centerService.fetchMyCenter();
      console.log('my Center', myCenter, rest);
      const mergedCenter = { ...centerValue, externalId: myCenter.id };
      setLoadingCenters(false);
      setActiveCenterById(mergedCenter.id, mergedCenter, [mergedCenter]);

      return centerValue;
    } catch (error) {
      setLoadingCenters(false);
      return [];
    }
  }, [activeCenter.id, setActiveCenterById]);

  const fetchCenterById = async (id:number) => {
    setLoadingCenters(true);
    try {
      const { center } = await centerService.fetchCenter(id);
      setCenter(center);
      return center;
    } catch (error) {
      setLoadingCenters(false);
      return [];
    }
  };

  const fetchActiveConfigCenter = () =>
    centerService.fetchCenterActiveConfig(Number(LOCAL_CENTER_EXTERNAL_ID()))
      .then((response: any) => {
        setActiveCenterConfig(response?.center_configuration);
        return response;
      }).catch((error: any) => error);

  const editCenterName = (id:number, data: any) => centerService.putCenter(id, data);

  const fetchUsersCenter = () => centerService.fetchUsersCenter();

  useMemo(() => {
    if (autoFetchs && !activeCenterConfig) {
      fetchActiveConfigCenter();
    }
  }, [activeCenterConfig, autoFetchs]);

  useEffect(() => {
    if (centerToken && centerToken !== LOCAL_ACTIVE_CENTER_TOKEN()) {
      setCoreAuthToken(centerToken);
      setLocalStorageItem(KEYS_LOCALSTORAGE.ACTIVE_CENTER_TOKEN, centerToken);
    }
    /* eslint-disable-next-line */
  }, [centerToken]);

  const refreshCenterData = () => {
    setCoreAuthToken(LOCAL_ACCOUNT_TOKEN());
  };

  const editTeacherPermission = async (permission: any, _centerId: any, teacherId: any) => {
    const body = {
      permission: {
        edit_dap: permission,
      },
    };
    const res = await centerService.editTeacherPermission(body, _centerId, teacherId);
    return res;
  };

  const updateCenterConfig = (body: any, idCenter: any) => centerService.putCenterConfig(body, idCenter);

  const getCenterConfig = (idCenter: any) =>
    centerService.getCenterConfig(idCenter).then((response: any) => {
      setCenterConfig(response);
      return response;
    });

  return {
    center,
    centerToken,
    centerList,
    activeCenter,
    setActiveCenter: centerService.setActiveCenter$,
    setActiveCenterById,
    fetchCenter,
    fetchCenters,
    fetchCenterById,
    editCenterName,
    loadingCenters,
    loadingCenters$,
    fetchDetailedCenters,
    refreshCenterData,
    fetchUsersCenter,
    editTeacherPermission,
    updateCenterConfig,
    getCenterConfig,
    fetchActiveConfigCenter,
    activeCenterConfig,
    centerConfig,
  };
}

export default useCenters;
